import React, { useState } from "react";
import { ColumnDef, RowSelectionState } from "@tanstack/react-table";
import Table from "../common/Table";
import { PencilIcon, PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import { useTemplateCategoriesInfinite } from "../../hooks/useTemplateCategoriesInfinite";
import {
  TemplateCategory,
  TemplateCategoryFormData,
  TemplateCategoryUpdateFormData,
  TemplateCategoryRequest,
} from "../../schemas/templateCategorySchema";
import TemplateCategoryForm from "../forms/TemplateCategoryForm";

const TemplateCategories: React.FC = () => {
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [selectedTemplateCategory, setSelectedTemplateCategory] = useState<
    TemplateCategory | undefined
  >();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [search, setSearch] = useState("");

  const {
    templateCategories,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    createTemplateCategory,
    updateTemplateCategory,
    deleteTemplateCategories,
  } = useTemplateCategoriesInfinite({ search });

  const handleEdit = (templateCategory: TemplateCategory) => {
    setSelectedTemplateCategory(templateCategory);
    setIsFormModalOpen(true);
  };

  const handleDelete = async (templateCategory: TemplateCategory) => {
    if (
      window.confirm(`Delete Template Category "${templateCategory.name}"?`)
    ) {
      await deleteTemplateCategories([templateCategory.id]);
    }
  };

  const handleBulkDelete = (ids: string[]) => {
    if (
      window.confirm(`Delete ${ids.length} selected template categor(y/ies)?`)
    ) {
      deleteTemplateCategories(ids);
      setRowSelection({});
    }
  };

  const handleSubmit = async (
    data: TemplateCategoryFormData | TemplateCategoryUpdateFormData,
  ) => {
    setIsSubmitting(true);
    const payload: TemplateCategoryRequest = {
      name: data.name || "",
      description: data.description,
    };
    try {
      if (selectedTemplateCategory?.id) {
        await updateTemplateCategory({
          id: selectedTemplateCategory.id,
          templateCategory: data as TemplateCategoryUpdateFormData,
        });
      } else {
        await createTemplateCategory(payload);
      }
    } finally {
      setSelectedTemplateCategory(undefined);
      setIsFormModalOpen(false);
      setIsSubmitting(false);
    }
  };

  const columns: ColumnDef<TemplateCategory>[] = [
    { accessorKey: "name", header: "Name" },
    { accessorKey: "description", header: "Description" },
  ];

  const actions = [
    {
      label: "Edit",
      icon: <PencilIcon className="h-4 w-4" />,
      onClick: handleEdit,
      variant: "success" as const,
    },
    {
      label: "Delete",
      icon: <TrashIcon className="h-4 w-4" />,
      onClick: handleDelete,
      variant: "danger" as const,
    },
  ];

  return (
    <>
      <div className="flex justify-between items-center mb-2">
        <h1 className="text-xl font-bold text-gray-800">Template Categories</h1>
        <button
          onClick={() => {
            setIsFormModalOpen(true);
          }}
          className="p-2 bg-primary-default text-white rounded hover:bg-primary-light"
        >
          <PlusIcon className="h-5 w-5" />
        </button>
      </div>

      <Table
        columns={columns}
        data={templateCategories as TemplateCategory[]}
        actions={actions}
        isLoading={isLoading}
        maxHeight="48vh"
        pagination
        searchable
        infinite
        hasNextPage={hasNextPage}
        isFetchingMore={isFetchingNextPage}
        onLoadMore={() => fetchNextPage()}
        onSearch={setSearch}
        selectable={true}
        getRowId={(templateCategory) => templateCategory.id}
        rowSelection={rowSelection}
        onRowSelectionChange={setRowSelection}
        bulkActions={[
          {
            label: "Delete Selected",
            icon: <TrashIcon className="h-4 w-4" />,
            onClick: handleBulkDelete,
            variant: "danger" as const,
          },
        ]}
      />

      {isFormModalOpen && (
        <TemplateCategoryForm
          isOpen={isFormModalOpen}
          onCancel={() => {
            setIsFormModalOpen(false);
            setSelectedTemplateCategory(undefined);
          }}
          initialData={selectedTemplateCategory}
          onSubmit={handleSubmit}
          isLoading={isSubmitting}
          isUpdate={!!selectedTemplateCategory}
        />
      )}
    </>
  );
};

export default TemplateCategories;
